import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottleSpinBoard from './BottleSpinBoard';
import { unlockSfx } from 'common/utils/sfx';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { IcTarget, IcBeer, IcParty, IcEdit, IcChampagne, IcClose, IcCheck, IconProps } from 'components/common/icons/EmojiIcons';
import styles from './BottleSpinModal.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface BottleParticipant {
    userNo: number;
    nickname: string;
}

export interface BottleSpinModalProps {
    open: boolean;
    initialParticipants?: BottleParticipant[]; // 모임 멤버 자동 로드용 (선택)
    onClose: () => void;
}

type Stage = 'setup' | 'spinning' | 'result';

/** 벌칙 묶음의 뼈대 — 글자는 화면에서 t() 로 뽑습니다.
 *  ⚠️ 모듈 로드 시점에 i18n.t 를 부르면 initI18n() 전에 평가돼
 *  returnObjects 가 undefined 로 돌아오고, 그 자리에서 화면이 죽습니다. */
const PENALTY_PRESETS: { key: string; labelKey: string; Icon: React.FC<IconProps>; itemsKey?: string }[] = [
    { key: 'NONE', labelKey: 'games.presetNone', Icon: IcTarget },
    { key: 'DRINK', labelKey: 'games.presetDrink', Icon: IcBeer, itemsKey: 'games.bottleDrinkItems' },
    { key: 'CASUAL', labelKey: 'games.presetCasual', Icon: IcParty, itemsKey: 'games.bottleCasualItems' },
    { key: 'CUSTOM', labelKey: 'games.customInput', Icon: IcEdit },
];

const BottleSpinModal: React.FC<BottleSpinModalProps> = ({ open, initialParticipants, onClose }) => {
    const { t } = useTranslation();
    const [nameInput, setNameInput] = useState('');
    // 모임 참여자 중 선택된 userNo 집합 (기본 전원)
    const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
    // 직접 추가한 외부 이름
    const [extraNames, setExtraNames] = useState<string[]>([]);
    const [presetKey, setPresetKey] = useState('DRINK');
    const [customPenalty, setCustomPenalty] = useState('');
    const [stage, setStage] = useState<Stage>('setup');
    const [seed, setSeed] = useState(1);
    const [targetIdx, setTargetIdx] = useState<number | null>(null);
    const [pickedPenalty, setPickedPenalty] = useState<string>('');

    useBackDismiss(open, onClose);
    if (!open) return null;

    const presetDef = PENALTY_PRESETS.find(p => p.key === presetKey) || PENALTY_PRESETS[0];
    const presetItems: string[] = (() => {
        if (!presetDef.itemsKey) return [];
        const arr = t(presetDef.itemsKey, { returnObjects: true });
        return Array.isArray(arr) ? (arr as string[]) : [];
    })();
    const preset = { ...presetDef, label: t(presetDef.labelKey), items: presetItems };

    // 최종 참여자 이름 목록 (회전/당첨용)
    const names: string[] = [
        ...((initialParticipants || []).filter(p => !excludedIds.has(p.userNo)).map(p => p.nickname)),
        ...extraNames,
    ];

    const toggleExclude = (userNo: number) => {
        setExcludedIds(prev => {
            const next = new Set(prev);
            if (next.has(userNo)) next.delete(userNo);
            else next.add(userNo);
            return next;
        });
    };

    const addName = () => {
        const t = nameInput.trim();
        if (!t) return;
        if (names.includes(t) || extraNames.includes(t)) { setNameInput(''); return; }
        if (names.length >= 10) return;
        setExtraNames([...extraNames, t]);
        setNameInput('');
    };
    const removeExtra = (idx: number) => setExtraNames(extraNames.filter((_, i) => i !== idx));

    const startSpin = () => {
        if (names.length < 2) return;
        unlockSfx();
        // 벌칙 선정 (지목만이면 skip)
        let pen = '';
        if (presetKey === 'CUSTOM') {
            pen = customPenalty.trim() || '';
        } else if (preset.items.length > 0) {
            pen = preset.items[Math.floor(Math.random() * preset.items.length)];
        }
        setPickedPenalty(pen);
        setTargetIdx(null);
        setSeed(Date.now() >>> 0);
        setStage('spinning');
    };

    const handleFinish = (idx: number) => {
        setTargetIdx(idx);
        setTimeout(() => setStage('result'), 500);
    };

    const restart = () => {
        setStage('setup');
        setTargetIdx(null);
        setPickedPenalty('');
    };

    return (
        <div className={cls("bottle-overlay")} onClick={stage !== 'spinning' ? onClose : undefined}>
            <div className={cls("bottle-modal")} onClick={e => e.stopPropagation()}>
                <header className={cls("bottle-header")}>
                    <span><IcChampagne size={18} /> {t('games.bottleTitle')}</span>
                    {stage !== 'spinning' && <button className={cls("bottle-close")} onClick={onClose}><IcClose size={18} /></button>}
                </header>

                {stage === 'setup' && (
                    <>
                        <div className={cls("bottle-sec-title")}>
                            {t('games.stepParticipants')}{' '}
                            <small>({t('games.tapToExclude', { count: names.length })})</small>
                        </div>
                        {/* 모임 참여자 — 기본 전원 포함, 탭해서 제외/복구 */}
                        {initialParticipants && initialParticipants.length > 0 && (
                            <ul className={cls("bottle-name-list")}>
                                {initialParticipants.map(p => {
                                    const excluded = excludedIds.has(p.userNo);
                                    return (
                                        <li
                                            key={p.userNo}
                                            className={cls(`bottle-chip ${excluded ? 'excluded' : ''}`)}
                                            onClick={() => toggleExclude(p.userNo)}
                                        >
                                            <span>{p.nickname}</span>
                                            <span className={cls("bottle-chip-mark")}>{excluded ? '＋' : <IcCheck size={14} />}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {/* 추가 입력 (외부 인원) */}
                        <div className={cls("bottle-input-row")}>
                            <input
                                className={cls("bottle-input")}
                                placeholder={initialParticipants && initialParticipants.length > 0 ? "외부 사람 추가" : "이름 입력 후 +"}
                                value={nameInput}
                                onChange={e => setNameInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') addName(); }}
                                maxLength={10}
                            />
                            <button className={cls("bottle-input-add")} onClick={addName} disabled={!nameInput.trim() || names.length >= 10}>+</button>
                        </div>
                        {extraNames.length > 0 && (
                            <ul className={cls("bottle-name-list")}>
                                {extraNames.map((nm, i) => (
                                    <li key={`ex-${i}`} className={cls("bottle-chip extra")}>
                                        <span>{nm}</span>
                                        <button className={cls("bottle-name-rm")} onClick={() => removeExtra(i)}><IcClose size={14} /></button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {names.length < 2 && (
                            <div className={cls("bottle-hint")}>{t('games.bottleMin2')}</div>
                        )}

                        <div className={cls("bottle-sec-title")}>{t('games.stepPenalty')}</div>
                        <div className={cls("bottle-presets")}>
                            {PENALTY_PRESETS.map(p => (
                                <button
                                    key={p.key}
                                    className={cls(`bottle-preset ${presetKey === p.key ? 'active' : ''}`)}
                                    onClick={() => setPresetKey(p.key)}
                                >
                                    <span className={cls("bottle-preset-icon")}><p.Icon size={20} /></span>
                                    <span>{t(p.labelKey)}</span>
                                </button>
                            ))}
                        </div>

                        {presetKey === 'CUSTOM' && (
                            <input
                                className={cls("bottle-input")}
                                placeholder={t('games.penaltyPlaceholder')}
                                value={customPenalty}
                                onChange={e => setCustomPenalty(e.target.value)}
                                maxLength={30}
                            />
                        )}
                        {presetKey !== 'CUSTOM' && preset.items.length > 0 && (
                            <div className={cls("bottle-penalty-preview")}>
                                <small>{t('games.randomOf', { count: preset.items.length })}</small>
                            </div>
                        )}

                        <div className={cls("bottle-actions")}>
                            <button className={cls("bottle-btn-cancel")} onClick={onClose}>{t('games.close')}</button>
                            <button className={cls("bottle-btn-spin")} onClick={startSpin} disabled={names.length < 2}>
                                <IcChampagne size={18} /> {t('games.spin')}
                            </button>
                        </div>
                    </>
                )}

                {(stage === 'spinning' || stage === 'result') && (
                    <>
                        <div className={cls("bottle-board-wrap")}>
                            <BottleSpinBoard
                                names={names}
                                seed={seed}
                                animating={stage === 'spinning'}
                                onFinish={handleFinish}
                                size={300}
                            />
                        </div>
                        {stage === 'result' && targetIdx !== null && (
                            <>
                                <div className={cls("bottle-result")}>
                                    <div className={cls("bottle-result-label")}><IcTarget size={16} /> {t('games.winner')}</div>
                                    <div className={cls("bottle-result-name")}>{names[targetIdx]}</div>
                                    {pickedPenalty && (
                                        <div className={cls("bottle-result-penalty")}>{pickedPenalty}</div>
                                    )}
                                </div>
                                <div className={cls("bottle-actions")}>
                                    <button className={cls("bottle-btn-cancel")} onClick={restart}>{t('games.retry')}</button>
                                    <button className={cls("bottle-btn-spin")} onClick={onClose}>{t('games.done')}</button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BottleSpinModal;
